import { Fragment } from "react";

import { GridTableEmptyList, GridTableScroll } from "../GridTable";
import PaginationCompact from "../PaginationCompact";
import { Pagination } from "@/modules/common/types/Pagination";

interface EntityGridListProps<T extends { id: string }> {
  data?: Pagination<T>;
  isLoading?: boolean;
  isFetching?: boolean;
  limit?: number;
  onChangePage: (nextPage: number) => void;
  onChangePageSize: (nextSize: number) => void;
  minWidthClassName?: string;
  renderHeader: () => React.ReactNode;
  renderRow: (item: T) => React.ReactNode;
  renderSkeleton: (limit: number) => React.ReactNode;
}

const EntityGridList = <T extends { id: string }>({
  data,
  isLoading = false,
  isFetching = false,
  limit = 20,
  onChangePage,
  onChangePageSize,
  minWidthClassName,
  renderHeader,
  renderRow,
  renderSkeleton,
}: EntityGridListProps<T>) => {
  if (isLoading || data === undefined) return <>{renderSkeleton(limit)}</>;
  if (!data.values.length) return <GridTableEmptyList />;

  return (
    <div className="w-full bg-white rounded-lg shadow overflow-hidden relative">
      {isFetching && <div className="absolute inset-0 bg-white/50 z-10 pointer-events-none transition-opacity" />}

      <GridTableScroll minWidthClassName={minWidthClassName}>
        {renderHeader()}

        <div className="divide-y divide-gray-100 md:divide-y-0">
          <div className="flex flex-col gap-3 p-3 md:p-0 md:gap-0">
            {data.values.map((item) => (
              <Fragment key={item.id}>{renderRow(item)}</Fragment>
            ))}
          </div>
        </div>
      </GridTableScroll>

      <div className="px-3 sm:px-5 py-3 border-t border-ui-border">
        <PaginationCompact metadata={data.metadata} onChangePage={onChangePage} onChangePageSize={onChangePageSize} />
      </div>
    </div>
  );
};

export default EntityGridList;
